import {
  denormalisedResponseEntities,
  updatedEntities,
  denormalisedEntities,
  onGetCloudnaryData,
} from '../../util/data';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';

import { fetchCurrentUser } from '../../ducks/user.duck';
import { algoliaCreateUpdateDeleteData } from '../EditListingPage/EditListingPage.duck';
import { DELETE_ALGOLIA_TYPE, LISTING_STATE_CLOSED, UPDATE_ALGOLIA_TYPE } from '../../util/types';
import { UploadImage } from '../../util/cloudinary';
import { getListingVariants, syncListingVariants } from '../../util/api';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 42 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 42;

const REACT_APP_ALGOLIA_LISTING_INDEX = process.env.REACT_APP_ALGOLIA_LISTING_INDEX;

const COPY_OF_PREFIX = 'Copy of ';

const getImageVariantInfo = listingImageConfig => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  const fieldsImage = [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`];

  return {
    fieldsImage,
    imageVariants: {
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    },
  };
};

const getImageUrlFromEntity = image => {
  const variants = image?.attributes?.variants || {};
  const preferredVariant = variants.default || variants['scaled-medium'];
  const fallbackVariant = Object.values(variants).find(v => v?.url);
  return preferredVariant?.url || fallbackVariant?.url || null;
};

const getOrderedSourceImageIds = listing => {
  const relationshipIds = listing?.relationships?.images?.data?.map(img => img.id.uuid) || [];
  const cloudImageIds = Object.keys(listing?.attributes?.publicData?.cloudListingImages || {});
  return relationshipIds.length ? relationshipIds : cloudImageIds;
};

const fileExtensionForContentType = contentType => {
  if (!contentType) {
    return 'jpg';
  }
  const [, subtype = 'jpeg'] = contentType.split('/');
  return subtype === 'jpeg' ? 'jpg' : subtype.split(';')[0];
};

const fetchImageAsFile = async (url, sourceImageId) => {
  if (typeof fetch !== 'function') {
    throw new Error('Image copying requires browser fetch support.');
  }

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to copy listing image ${sourceImageId}.`);
  }

  const blob = await response.blob();
  const type = blob.type || response.headers.get('content-type') || 'image/jpeg';
  const extension = fileExtensionForContentType(type);
  return new File([blob], `${sourceImageId}.${extension}`, { type });
};

const copyListingImages = async (listing, sdk, imageVariantInfo) => {
  const publicData = listing?.attributes?.publicData || {};
  const sourceCloudImages = publicData.cloudListingImages || {};
  const sourceImageIds = getOrderedSourceImageIds(listing);
  const sourceImagesById = (listing?.images || []).reduce((imagesById, image) => {
    return { ...imagesById, [image.id.uuid]: image };
  }, {});

  const copiedImages = [];
  const copiedCloudImages = {};
  const imageIdMap = {};

  for (const sourceImageId of sourceImageIds) {
    const sourceUrl =
      sourceCloudImages[sourceImageId] || getImageUrlFromEntity(sourceImagesById[sourceImageId]);
    if (!sourceUrl) {
      throw new Error(`Missing source URL for listing image ${sourceImageId}.`);
    }

    const file = await fetchImageAsFile(sourceUrl, sourceImageId);
    const uploadResponse = await sdk.images.upload(
      { image: file },
      {
        expand: true,
        'fields.image': imageVariantInfo.fieldsImage,
        ...imageVariantInfo.imageVariants,
      }
    );
    const image = uploadResponse.data.data;
    const newImageId = image.id.uuid;
    const cloudinaryResponse = await UploadImage({
      id: newImageId,
      file,
      folder: 'listing-images',
    });
    const copiedCloudUrl = cloudinaryResponse
      ? onGetCloudnaryData(cloudinaryResponse)?.secure_url
      : null;

    if (!copiedCloudUrl) {
      throw new Error(`Failed to copy listing image ${sourceImageId} to Cloudinary.`);
    }

    imageIdMap[sourceImageId] = newImageId;
    copiedCloudImages[newImageId] = copiedCloudUrl;
    copiedImages.push({ ...image, imageId: image.id, cloudListingImage: copiedCloudUrl });
  }

  const sourceHeroImageId = publicData.heroImageId;
  const copiedHeroImageId =
    imageIdMap[sourceHeroImageId] || copiedImages[0]?.imageId?.uuid || copiedImages[0]?.id?.uuid;

  return {
    copiedImages,
    copiedCloudImages,
    copiedHeroImageId,
  };
};

const normalizeMapLikeObject = value => {
  if (!value) {
    return {};
  }

  if (value instanceof Map) {
    return Object.fromEntries(value.entries());
  }

  return { ...value };
};

const buildVariantCopyPayload = (variants = [], config = null) => {
  const selectedOptions = normalizeMapLikeObject(config?.selectedOptions);
  const fieldLabels = normalizeMapLikeObject(config?.fieldLabels);
  const fieldKeysByLabel = Object.entries(fieldLabels).reduce((keysByLabel, [key, label]) => {
    return { ...keysByLabel, [label]: key };
  }, {});

  return variants.reduce(
    (payload, variant, index) => {
      const attributes = Array.isArray(variant.attributes) ? variant.attributes : [];
      const metadata = attributes.reduce((acc, attribute) => {
        const fieldKey = fieldKeysByLabel[attribute.name] || attribute.name;
        return {
          ...acc,
          [fieldKey]: {
            id: attribute.optionId,
            label: attribute.value,
            fieldLabel: attribute.name,
          },
        };
      }, {});

      const comboKey =
        attributes
          .map(
            attribute =>
              `${fieldKeysByLabel[attribute.name] || attribute.name}:${attribute.optionId ??
                attribute.value}`
          )
          .sort()
          .join('|') || `variant-${index}`;

      return {
        ...payload,
        variantsData: {
          ...payload.variantsData,
          [comboKey]: variant.stock,
        },
        variantsMetadata: {
          ...payload.variantsMetadata,
          [comboKey]: metadata,
        },
      };
    },
    {
      variantsData: {},
      variantsMetadata: {},
      selectedOptions,
      groupByField: config?.groupByField || null,
    }
  );
};

const buildDuplicateListingValues = (listing, copiedMedia) => {
  const {
    title = '',
    description,
    price,
    publicData = {},
    privateData,
    // metadata,
    geolocation,
    availabilityPlan,
  } = listing.attributes || {};

  return {
    title: `${COPY_OF_PREFIX}${title}`,
    ...(description ? { description } : {}),
    ...(price ? { price } : {}),
    ...(privateData ? { privateData } : {}),
    // ...(metadata ? { metadata } : {}),
    ...(geolocation ? { geolocation } : {}),
    ...(availabilityPlan ? { availabilityPlan } : {}),
    publicData: {
      ...publicData,
      cloudListingImages: copiedMedia.copiedCloudImages,
      heroImageId: copiedMedia.copiedHeroImageId,
      isDeleted: false,
    },
    images: copiedMedia.copiedImages.map(image => image.imageId || image.id),
  };
};

// ================ Action types ================ //

export const FETCH_LISTINGS_REQUEST = 'app/ManageListingsPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/ManageListingsPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/ManageListingsPage/FETCH_LISTINGS_ERROR';

export const OPEN_LISTING_REQUEST = 'app/ManageListingsPage/OPEN_LISTING_REQUEST';
export const OPEN_LISTING_SUCCESS = 'app/ManageListingsPage/OPEN_LISTING_SUCCESS';
export const OPEN_LISTING_ERROR = 'app/ManageListingsPage/OPEN_LISTING_ERROR';

export const DELETE_LISTING_REQUEST = 'app/ManageListingsPage/DELETE_LISTING_REQUEST';
export const DELETE_LISTING_SUCCESS = 'app/ManageListingsPage/DELETE_LISTING_SUCCESS';
export const DELETE_LISTING_ERROR = 'app/ManageListingsPage/DELETE_LISTING_ERROR';

export const DUPLICATE_LISTING_REQUEST = 'app/ManageListingsPage/DUPLICATE_LISTING_REQUEST';
export const DUPLICATE_LISTING_SUCCESS = 'app/ManageListingsPage/DUPLICATE_LISTING_SUCCESS';
export const DUPLICATE_LISTING_ERROR = 'app/ManageListingsPage/DUPLICATE_LISTING_ERROR';

export const CLOSE_LISTING_REQUEST = 'app/ManageListingsPage/CLOSE_LISTING_REQUEST';
export const CLOSE_LISTING_SUCCESS = 'app/ManageListingsPage/CLOSE_LISTING_SUCCESS';
export const CLOSE_LISTING_ERROR = 'app/ManageListingsPage/CLOSE_LISTING_ERROR';

export const ADD_OWN_ENTITIES = 'app/ManageListingsPage/ADD_OWN_ENTITIES';
export const CLEAR_OPEN_LISTING_ERROR = 'app/ManageListingsPage/CLEAR_OPEN_LISTING_ERROR';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  queryParams: null,
  queryInProgress: false,
  queryListingsError: null,
  currentPageResultIds: [],
  ownEntities: {},
  openingListing: null,
  openingListingError: null,
  closingListing: null,
  closingListingError: null,
  deleteListing: null,
  deleteListingError: null,
  duplicateListing: null,
  duplicateListingError: null,
};

const resultIds = data => data.data.map(l => l.id);

const merge = (state, sdkResponse) => {
  const apiResponse = sdkResponse.data;
  return {
    ...state,
    ownEntities: updatedEntities({ ...state.ownEntities }, apiResponse),
  };
};

const updateListingAttributes = (state, listingEntity) => {
  const oldListing = state.ownEntities.ownListing[listingEntity.id.uuid];
  const updatedListing = { ...oldListing, attributes: listingEntity.attributes };
  const ownListingEntities = {
    ...state.ownEntities.ownListing,
    [listingEntity.id.uuid]: updatedListing,
  };
  return {
    ...state,
    ownEntities: { ...state.ownEntities, ownListing: ownListingEntities },
  };
};

const manageListingsPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case CLEAR_OPEN_LISTING_ERROR:
      return {
        ...state,
        openingListing: null,
        openingListingError: null,
      };

    case FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        queryParams: payload.queryParams,
        queryInProgress: true,
        queryListingsError: null,
        currentPageResultIds: [],
      };
    case FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        queryInProgress: false,
      };
    case FETCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, queryInProgress: false, queryListingsError: payload };

    case OPEN_LISTING_REQUEST:
      return {
        ...state,
        openingListing: payload.listingId,
        openingListingError: null,
      };
    case OPEN_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        openingListing: null,
      };
    case OPEN_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        openingListing: null,
        openingListingError: {
          listingId: state.openingListing,
          error: payload,
        },
      };
    }

    case DELETE_LISTING_REQUEST:
      return {
        ...state,
        deleteListing: payload.listingId,
        deleteListingError: null,
      };
    case DELETE_LISTING_SUCCESS:
      return {
        deleteListing: null,
        deleteListingError: null,
      };
    case DELETE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        deleteListing: null,
        deleteListingError: {
          listingId: state.deleteListing,
          error: payload,
        },
      };
    }

    case DUPLICATE_LISTING_REQUEST:
      return {
        ...state,
        duplicateListing: payload.listingId,
        duplicateListingError: null,
      };
    case DUPLICATE_LISTING_SUCCESS:
      return {
        ...state,
        duplicateListing: null,
        duplicateListingError: null,
      };
    case DUPLICATE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        duplicateListing: null,
        duplicateListingError: {
          listingId: state.duplicateListing,
          error: payload,
        },
      };
    }

    case CLOSE_LISTING_REQUEST:
      return {
        ...state,
        closingListing: payload.listingId,
        closingListingError: null,
      };
    case CLOSE_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        closingListing: null,
      };
    case CLOSE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        closingListing: null,
        closingListingError: {
          listingId: state.closingListing,
          error: payload,
        },
      };
    }

    case ADD_OWN_ENTITIES:
      return merge(state, payload);

    default:
      return state;
  }
};

export default manageListingsPageReducer;

// ================ Selectors ================ //

/**
 * Get the denormalised own listing entities with the given IDs
 *
 * @param {Object} state the full Redux store
 * @param {Array<UUID>} listingIds listing IDs to select from the store
 */
export const getOwnListingsById = (state, listingIds) => {
  const { ownEntities } = state.ManageListingsPage;
  const resources = listingIds.map(id => ({
    id,
    type: 'ownListing',
  }));
  const throwIfNotFound = false;
  return denormalisedEntities(ownEntities, resources, throwIfNotFound);
};

// ================ Action creators ================ //

export const clearOpenListingError = () => ({
  type: CLEAR_OPEN_LISTING_ERROR,
});

// This works the same way as addMarketplaceEntities,
// but we don't want to mix own listings with searched listings
// (own listings data contains different info - e.g. exact location etc.)
export const addOwnEntities = sdkResponse => ({
  type: ADD_OWN_ENTITIES,
  payload: sdkResponse,
});

export const openListingRequest = listingId => ({
  type: OPEN_LISTING_REQUEST,
  payload: { listingId },
});

export const openListingSuccess = response => ({
  type: OPEN_LISTING_SUCCESS,
  payload: response.data,
});

export const openListingError = e => ({
  type: OPEN_LISTING_ERROR,
  error: true,
  payload: e,
});

export const closeListingRequest = listingId => ({
  type: CLOSE_LISTING_REQUEST,
  payload: { listingId },
});

export const closeListingSuccess = response => ({
  type: CLOSE_LISTING_SUCCESS,
  payload: response.data,
});

export const closeListingError = e => ({
  type: CLOSE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const queryListingsRequest = queryParams => ({
  type: FETCH_LISTINGS_REQUEST,
  payload: { queryParams },
});

export const queryListingsSuccess = response => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const queryListingsError = e => ({
  type: FETCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const deleteListingRequest = listingId => ({
  type: DELETE_LISTING_REQUEST,
  payload: { listingId },
});

export const deleteListingSuccess = response => ({
  type: DELETE_LISTING_SUCCESS,
  payload: response.data,
});

export const deleteListingError = e => ({
  type: DELETE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const duplicateListingRequest = listingId => ({
  type: DUPLICATE_LISTING_REQUEST,
  payload: { listingId },
});

export const duplicateListingSuccess = response => ({
  type: DUPLICATE_LISTING_SUCCESS,
  payload: response.data,
});

export const duplicateListingError = e => ({
  type: DUPLICATE_LISTING_ERROR,
  error: true,
  payload: e,
});

// Throwing error for new (loadData may need that info)
export const queryOwnListings = queryParams => (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest(queryParams));

  const { perPage, ...rest } = queryParams;
  const params = { ...rest, perPage, pub_isDeleted: false };

  return sdk.ownListings
    .query(params)
    .then(response => {
      dispatch(addOwnEntities(response));
      dispatch(queryListingsSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(queryListingsError(storableError(e)));
      throw e;
    });
};

export const closeListing = listingId => (dispatch, getState, sdk) => {
  dispatch(closeListingRequest(listingId));

  return sdk.ownListings
    .close({ id: listingId }, { expand: true })
    .then(response => {
      dispatch(closeListingSuccess(response));

      // <<<<<<<<<<<<<<<< upadate data in algolia >>>>>>>>>>>>>>>>>>>>>>>>>
      try {
        const listing = response.data.data;
        const currentUser = getState().user.currentUser;
        const updateParams = { state: listing.attributes.state };
        algoliaCreateUpdateDeleteData(
          UPDATE_ALGOLIA_TYPE,
          REACT_APP_ALGOLIA_LISTING_INDEX,
          listing,
          currentUser,
          updateParams
        );
      } catch (e) {
        console.log(e, '&&& &&& => e');
      }

      return response;
    })
    .catch(e => {
      dispatch(closeListingError(storableError(e)));
    });
};

export const openListing = listingId => (dispatch, getState, sdk) => {
  dispatch(openListingRequest(listingId));

  return sdk.ownListings
    .open({ id: listingId }, { expand: true })
    .then(response => {
      dispatch(openListingSuccess(response));
      // <<<<<<<<<<<<<<<< upadate data in algolia >>>>>>>>>>>>>>>>>>>>>>>>>
      try {
        const listing = response.data.data;
        const currentUser = getState().user.currentUser;
        const updateParams = { state: listing.attributes.state };
        algoliaCreateUpdateDeleteData(
          UPDATE_ALGOLIA_TYPE,
          REACT_APP_ALGOLIA_LISTING_INDEX,
          listing,
          currentUser,
          updateParams
        );
      } catch (e) {
        console.log(e, '&&& &&& => e');
      }

      return response;
    })
    .catch(e => {
      dispatch(openListingError(storableError(e)));
    });
};

export const deleteListing = (queryParams, listingId, params = {}) => async (
  dispatch,
  getState,
  sdk
) => {
  const { isDraft, isClosed = false } = params;
  dispatch(deleteListingRequest(listingId));
  // if listing is closed then opn first
  isClosed && (await sdk.ownListings.open({ id: listingId }, { expand: true }));
  // if listing in draft then discard else update
  const query = isDraft
    ? sdk.ownListings.discardDraft({ id: listingId })
    : sdk.ownListings.update({ id: listingId, publicData: { isDeleted: true } });
  return query
    .then(async response => {
      // now close that listing
      isDraft ? null : await sdk.ownListings.close({ id: listingId }, { expand: true });
      setTimeout(() => {
        dispatch(queryOwnListings({ ...queryParams }));
        dispatch(deleteListingSuccess(response));
      }, 1000);

      // <<<<<<<<<<<<<<<< upadate data in algolia >>>>>>>>>>>>>>>>>>>>>>>>>
      try {
        const listing = response.data.data;
        const currentUser = getState().user.currentUser;
        const updateParams = { state: LISTING_STATE_CLOSED, isDeleted: true };
        algoliaCreateUpdateDeleteData(
          DELETE_ALGOLIA_TYPE,
          REACT_APP_ALGOLIA_LISTING_INDEX,
          listing,
          currentUser,
          updateParams
        );
      } catch (e) {
        console.log(e, '&&& &&& => e');
      }

      return response;
    })
    .catch(e => {
      dispatch(deleteListingError(storableError(e)));
    });
};

export const duplicateListing = (queryParams, listingId, config) => async (
  dispatch,
  getState,
  sdk
) => {
  dispatch(duplicateListingRequest(listingId));

  const imageVariantInfo = getImageVariantInfo(config.layout.listingImage);
  const showQueryParams = {
    expand: true,
    include: ['author', 'images', 'currentStock'],
    'fields.image': imageVariantInfo.fieldsImage,
    ...imageVariantInfo.imageVariants,
    'limit.images': 100,
  };

  try {
    const sourceResponse = await sdk.ownListings.show({ id: listingId, ...showQueryParams });
    const [sourceListing] = denormalisedResponseEntities(sourceResponse);
    const publicData = sourceListing?.attributes?.publicData || {};

    if (publicData.source === 'shopify') {
      throw new Error('Shopify-sourced listings cannot be duplicated.');
    }

    console.log(sourceListing, '&&& &&& => sourceListing');
    
    const copiedMedia = await copyListingImages(sourceListing, sdk, imageVariantInfo);
    const createDraftValues = buildDuplicateListingValues(sourceListing, copiedMedia);

    console.log(createDraftValues, '&&& &&& => createDraftValues');
    

    const createResponse = await sdk.ownListings.createDraft(createDraftValues, showQueryParams);
    const copiedListing = createResponse.data.data;
    const copiedListingId = copiedListing.id;
    const sourceStock = sourceListing?.currentStock?.attributes?.quantity;

    if (typeof sourceStock === 'number') {
      await sdk.stock.compareAndSet(
        { listingId: copiedListingId, oldTotal: null, newTotal: sourceStock },
        { expand: true }
      );
    }

    const variantsResponse = await getListingVariants(listingId.uuid);
    const variants = variantsResponse?.variants || [];
    if (variants.length > 0) {
      const copiedListingAuthorId =
        copiedListing.relationships?.author?.data?.id?.uuid ||
        getState().user.currentUser?.id?.uuid;
      const variantPayload = buildVariantCopyPayload(variants, variantsResponse.config);

      await syncListingVariants({
        listingId: copiedListingId.uuid,
        vendorId: copiedListingAuthorId,
        ...variantPayload,
      });
    }

    dispatch(addOwnEntities(createResponse));
    await dispatch(queryOwnListings({ ...queryParams }));
    dispatch(duplicateListingSuccess(createResponse));
    return createResponse;
  } catch (e) {
    dispatch(duplicateListingError(storableError(e)));
    return null;
  }
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const queryParams = parse(search);
  const page = queryParams.page || 1;
  dispatch(clearOpenListingError());

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return Promise.all([
    dispatch(fetchCurrentUser()),
    dispatch(
      queryOwnListings({
        ...queryParams,
        page,
        perPage: RESULT_PAGE_SIZE,
        include: ['images', 'currentStock'],
        'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
        ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
        'limit.images': 1,
      })
    ),
  ])
    .then(response => {
      // const currentUser = response[0]?.data?.data;
      const ownListings = response[1]?.data?.data;
      return ownListings;
    })
    .catch(e => {
      throw e;
    });
};
